import { encodeEscPosPlainText, type ThermalPrintDocument } from '@vuelto/print';
import { showInfo } from '../../shared/ui/appToast';
import { getBranchReceiptPrintConfig } from '../infra/printConfigRepo';
import { recordPrintOutcome } from '../infra/printStatusRepo';
import { browserPrintTransport } from '../infra/transports/browserPrintTransport';
import { webAgentPrintTransport } from '../infra/transports/webAgentPrintTransport';
import type { PrintJobResult, ReceiptPrintMode } from '../domain/printConfig';

export type PrintThermalDocumentOptions = {
  /** Si true y mode=off, no imprime (solo tickets de venta). */
  blockWhenModeOff?: boolean;
  /** Fuerza un transporte independiente del modo de tickets. */
  transportOverride?: ReceiptPrintMode;
  /** Si false, un fallo del agente devuelve error en vez de abrir vista previa. */
  fallbackToBrowser?: boolean;
  /** Destino explícito del agente (ej. impresora de cocina). Default: printerName de la config. */
  printerOverride?: string;
};

function resolveEscpos(document: ThermalPrintDocument): Uint8Array {
  return document.escpos ?? encodeEscPosPlainText(document.text);
}

function resolveTransport(mode: ReceiptPrintMode, blockWhenModeOff: boolean): ReceiptPrintMode {
  if (mode === 'off' && !blockWhenModeOff) return 'browser';
  return mode;
}

export async function printThermalDocumentJob(
  branchId: string,
  document: ThermalPrintDocument,
  options?: PrintThermalDocumentOptions
): Promise<PrintJobResult> {
  const blockWhenModeOff = options?.blockWhenModeOff === true;
  const config = await getBranchReceiptPrintConfig(branchId);
  const transport = options?.transportOverride ?? resolveTransport(config.mode, blockWhenModeOff);
  if (config.mode === 'off' && blockWhenModeOff && !options?.transportOverride) {
    return { ok: false, error: 'Impresión de tickets desactivada en este dispositivo.', transport: 'off' };
  }
  const payload: ThermalPrintDocument = {
    html: document.html,
    text: document.text,
    escpos: resolveEscpos(document),
  };

  try {
    if (transport === 'agent') {
      try {
        await webAgentPrintTransport({
          agentUrl: config.agentUrl,
          document: payload,
          printerName: options?.printerOverride ?? config.printerName,
        });
        void recordPrintOutcome(branchId, { transport: 'agent' });
        return { ok: true, transport: 'agent' };
      } catch (e) {
        if (options?.fallbackToBrowser === false) {
          throw e;
        }
        // Agente caído o impresora inalcanzable → caer a Vista previa, no bloquear el cobro.
        const reason = e instanceof Error ? e.message : 'agente no disponible';
        await browserPrintTransport({
          html: payload.html,
          text: payload.text,
          widthMm: config.widthMm,
        });
        const warning = `Impresión directa falló (${reason}); salió por vista previa.`;
        void recordPrintOutcome(branchId, { transport: 'browser', fallbackWarning: warning });
        return {
          ok: true,
          transport: 'browser',
          warning,
        };
      }
    }
    await browserPrintTransport({
      html: payload.html,
      text: payload.text,
      widthMm: config.widthMm,
    });
    return { ok: true, transport: 'browser' };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'No se pudo imprimir.';
    return { ok: false, error: message, transport };
  }
}

/** Imprime documento térmico; lanza si falla (ignora cancelación de diálogo). */
export async function printThermalDocument(
  branchId: string,
  document: ThermalPrintDocument,
  options?: PrintThermalDocumentOptions
): Promise<void> {
  const result = await printThermalDocumentJob(branchId, document, options);
  if (!result.ok) {
    throw new Error(result.error);
  }
  // Comandas/precuenta llegan por acá: sin esto el fallback a vista previa
  // pasaba en silencio y en cocina simplemente "no salió".
  if (result.warning) showInfo('Impresión', result.warning);
}

export function isPrintUserCancellation(message: string): boolean {
  return message.toLowerCase().includes('cancel');
}
